import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import { INSTANT_BUY_URL } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { unitMapping } from '@sorare/core/src/lib/style';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { TeamsCarousel_anyTeam } from './__generated__/index.graphql';

const ScrollableWrapper = styled.div`
  position: relative;
  overflow: hidden;
  z-index: 1;
  width: 100%;
  padding: 0 var(--double-unit);
  @container ${laptopAndAbove} {
    padding: 0 var(--quadruple-unit);
  }
`;

type Props = {
  clubs: TeamsCarousel_anyTeam[];
  itemToDisplay?: number;
  itemGap?: keyof typeof unitMapping;
  size?: number;
};

export const TeamsCarousel = ({
  clubs,
  itemToDisplay = 5,
  itemGap = 2,
  size,
}: Props) => {
  const { sport } = useSportContext();

  const orderedClubs = [...clubs].sort((a, b) => {
    return a.shortName.localeCompare(b.shortName);
  });
  return (
    <ScrollableWrapper>
      <Scrollable itemToDisplay={itemToDisplay} itemGap={itemGap} withMask>
        {orderedClubs?.map(club => (
          <ButtonBase
            key={club.slug}
            to={`${INSTANT_BUY_URL[sport]}?${buildFilterQuery({
              [SEARCH_PARAMS.CLUB]: encodeURI(`${club.name}|${club.slug}`),
            })}`}
          >
            <TeamAvatar key={club.slug} team={club} size={size} />
          </ButtonBase>
        ))}
      </Scrollable>
    </ScrollableWrapper>
  );
};

TeamsCarousel.fragments = {
  anyTeam: gql`
    fragment TeamsCarousel_anyTeam on TeamInterface {
      slug
      name
      shortName
      ...TeamAvatar_team
    }
    ${TeamAvatar.fragments.team}
  ` as TypedDocumentNode<TeamsCarousel_anyTeam>,
};
